import React, { useCallback } from "react";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";
import { DietDairyTypes } from "../../../smart/LCF401/DietDiary/DietDiary.constants";
import {
  TRIHEPTANOIN_ITEMS,
  FOOD_DRINK_ITEMS,
  MCT_OIL_ITEMS,
  MCT_POWDER_ITEMS,
  GLYCOSADE_ITEMS,
  CORNSTARCH_ITEMS,
} from "./FrequentlyForgottenItems.constants";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  color: ${(props) => props.theme?.blackLite};
  font-weight: 600;
  font-size: 18px;
`;

const TitleDivider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme?.blackLite};
`;

const InfoContainer = styled.div`
  background-color: ${(props) => props.theme?.freshOcean};
  width: 100%;
  margin-top: 25px;
  padding: 15px 15px 15px 0px;

  .innerInfo {
    padding-left: 20px;
  }
`;

const ItemTitle = styled.span`
  font-weight: 600;
`;

const Info = styled.ul`
  width: 100%;
  color: ${(props) => props.theme?.blackLite};
  list-style-image: none;
  padding-left: 35px;

  li {
    margin: 5px 0;
  }

  & > .title,
  .icon {
    font-weight: 600;
    list-style-type: none;
  }

  .title,
  .icon {
    margin-left: -15px;
  }

  .icon {
    display: inline-flex;
    align-items: center;
  }
`;

const Icon = styled.img`
  margin-right: 3px;
  width: 39px;
  height: 39px;
`;

const FrequentlyForgottenItems = (props) => {
  const { dietDiaryType } = props;

  const { t } = useTranslation();

  const getItems = useCallback(() => {
    switch (dietDiaryType) {
      case DietDairyTypes.MCT_OIL:
        return MCT_OIL_ITEMS;
      case DietDairyTypes.MCT_POWDER:
        return MCT_POWDER_ITEMS;
      case DietDairyTypes.TRIHEPTANOIN:
        return TRIHEPTANOIN_ITEMS;
      case DietDairyTypes.GLYCOSADE:
        return GLYCOSADE_ITEMS;
      case DietDairyTypes.CORNSTARCH:
        return CORNSTARCH_ITEMS;
      default:
        return FOOD_DRINK_ITEMS;
    }
  }, [dietDiaryType]);

  return (
    <Container data-testid="frequently-forgotten-items">
      <Title>
        {t("contentText.dietDiary.addMeal.frequentlyForgottenItems.title")}
      </Title>
      <TitleDivider />

      <InfoContainer>
        <Info>
          {getItems().map((item, index) => {
            if (item.type === "text") {
              return (
                <li key={`item_${index}`}>
                  {item.translatable ? t(item.description) : item.description}
                </li>
              );
            } else if (item.type === "multi-text") {
              return (
                <li key={`item_${index}`}>
                  <ItemTitle>
                    {item.translatable ? t(item.title) : item.title}
                  </ItemTitle>
                  <Info className="innerInfo">
                    {item.description.map((desc, i) => (
                      <li key={`item_${index}_subitem_${i}`}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: item.translatable ? t(desc) : desc,
                          }}
                        />
                      </li>
                    ))}
                  </Info>
                </li>
              );
            } else if (item.type === "title") {
              return (
                <li className="title" key={`item_${index}`}>
                  {item.translatable ? t(item.description) : item.description}
                </li>
              );
            } else if (item.type === "icon") {
              return (
                <li className="icon" key={`item_${index}`}>
                  <Icon
                    src={item.icon}
                    alt={
                      item.translatable ? t(item.description) : item.description
                    }
                  />
                  <span>
                    {item.translatable ? t(item.description) : item.description}
                  </span>
                </li>
              );
            }
            return null;
          })}
        </Info>
      </InfoContainer>
    </Container>
  );
};

FrequentlyForgottenItems.propTypes = {
  dietDiaryType: PropTypes.oneOf(Object.values(DietDairyTypes)),
};

FrequentlyForgottenItems.defaultProps = {
  dietDiaryType: DietDairyTypes.FOOD_DRINK,
};

export default withTheme(FrequentlyForgottenItems);
